import { Player, TeamFormationProps } from '@dreamteam-monorepo/ui';
import { BETeamDetail, BETeamScoringDetail } from '@/lib/network';
import { toUiPlayerFromTeamPlayerDetail } from '@/lib/players/toUiPlayer';
import { Formations } from '@/stores/UserTeamStore';

const parseFormation = (
  formationValue: string | null | undefined
): TeamFormationProps['formation'] | undefined => {
  if (!formationValue) {
    return undefined;
  }

  const parts = formationValue
    .split('-')
    .map(value => Number(value))
    .filter(value => Number.isFinite(value));

  if (parts.length !== 3 && parts.length !== 4) {
    return undefined;
  }

  const [gk, def, mid, str] = parts.length === 3 ? [1, ...parts] : parts;
  if (gk <= 0 || def < 0 || mid < 0 || str < 0) {
    return undefined;
  }

  return [gk, def, mid, str];
};

const buildPlayersVersion = (team: Pick<BETeamDetail | BETeamScoringDetail, 'players'>) => {
  return [...team.players]
    .sort((leftPlayer, rightPlayer) => {
      const leftSlot = leftPlayer.positionSlot ?? Number.MAX_SAFE_INTEGER;
      const rightSlot = rightPlayer.positionSlot ?? Number.MAX_SAFE_INTEGER;

      if (leftSlot !== rightSlot) {
        return leftSlot - rightSlot;
      }

      return String(leftPlayer.fantasyPlayerId).localeCompare(String(rightPlayer.fantasyPlayerId));
    })
    .map(teamPlayer => {
      const gameweekPoints =
        'gameweekPoints' in teamPlayer && typeof teamPlayer.gameweekPoints === 'number'
          ? teamPlayer.gameweekPoints
          : 'null';
      return `${teamPlayer.positionSlot ?? 'x'}:${teamPlayer.fantasyPlayerId}:${teamPlayer.isCaptain ? 1 : 0}:${teamPlayer.isViceCaptain ? 1 : 0}:${gameweekPoints}`;
    })
    .join('|');
};

const buildPendingTransfersVersion = (team: Partial<Pick<BETeamDetail, 'pendingTransfers'>>) => {
  return [...(team.pendingTransfers ?? [])]
    .sort((leftTransfer, rightTransfer) =>
      String(leftTransfer.id).localeCompare(String(rightTransfer.id))
    )
    .map(transfer => {
      return [
        transfer.id,
        transfer.transferOutId,
        transfer.transferInId,
        transfer.gameweekId ?? 'null',
        transfer.matchdayId ?? 'null',
        transfer.targetMatchdayId ?? 'null',
        transfer.updatedFormation ?? 'null',
        transfer.scheduledAt,
      ].join(':');
    })
    .join('|');
};

const buildPendingCaptaincyVersion = (
  team: Partial<Pick<BETeamDetail, 'pendingCaptaincyChanges'>>
) => {
  return [...(team.pendingCaptaincyChanges ?? [])]
    .sort((leftChange, rightChange) => String(leftChange.id).localeCompare(String(rightChange.id)))
    .map(change => {
      return [
        change.id,
        change.fantasyPlayerId,
        change.fromRole,
        change.toRole,
        change.gameweekId ?? 'null',
        change.matchdayId ?? 'null',
        change.targetMatchdayId ?? 'null',
        change.scheduledAt,
      ].join(':');
    })
    .join('|');
};

const buildCurrentSubstitutionVersion = (
  team: Partial<Pick<BETeamDetail, 'currentMatchdaySubstitution'>>
) => {
  const currentSubstitution = team.currentMatchdaySubstitution;
  if (!currentSubstitution) {
    return '';
  }

  return [
    currentSubstitution.id,
    currentSubstitution.matchdayId ?? 'null',
    currentSubstitution.gameweekId ?? 'null',
    currentSubstitution.subOutFantasyPlayerId,
    currentSubstitution.subInFantasyPlayerId,
    currentSubstitution.appliedAt,
  ].join(':');
};

export const buildTeamHydrationVersion = (team: BETeamDetail | BETeamScoringDetail) => {
  const teamWithOptionals = team as BETeamDetail;
  return [
    team.id,
    teamWithOptionals.updatedAt ?? 'null',
    String(team.budgetRemaining ?? 'null'),
    String(team.transfersUsed ?? 'null'),
    team.teamName,
    team.formation ?? 'null',
    buildPlayersVersion(team),
    buildPendingTransfersVersion(teamWithOptionals),
    buildPendingCaptaincyVersion(teamWithOptionals),
    buildCurrentSubstitutionVersion(teamWithOptionals),
  ].join(':');
};

export const buildTeamHydrationState = (
  team: BETeamDetail | BETeamScoringDetail,
  players: Player[],
  options?: { preferTeamPlayers?: boolean }
) => {
  const parsedFormation = parseFormation(team.formation) ?? Formations[0].formation;
  const playersById = new Map<string, Player>();
  const preferTeamPlayers = options?.preferTeamPlayers ?? false;
  for (const player of players) {
    playersById.set(String(player.id), player);
  }

  for (const teamPlayer of team.players) {
    if (!teamPlayer.player) {
      continue;
    }

    const hydratedPlayer = toUiPlayerFromTeamPlayerDetail(teamPlayer.player);
    if (!hydratedPlayer) {
      continue;
    }

    const effectiveMatchdayPoints =
      'gameweekPoints' in teamPlayer && typeof teamPlayer.gameweekPoints === 'number'
        ? teamPlayer.gameweekPoints
        : hydratedPlayer.matchdayPoints;

    const teamScopedPlayer = preferTeamPlayers
      ? {
          ...hydratedPlayer,
          gameweekPoints: effectiveMatchdayPoints,
          matchdayPoints: effectiveMatchdayPoints,
        }
      : hydratedPlayer;

    // Historical views should keep the frozen team-detail player identity,
    // while current views still prefer the live pool player for fresh pricing
    // and status.
    if (preferTeamPlayers || !playersById.has(String(hydratedPlayer.id))) {
      playersById.set(String(hydratedPlayer.id), teamScopedPlayer);
    }
  }

  const playersBySlot = new Map<number, Player | undefined>();
  let captainPlayerId: Player['id'] | undefined;
  let viceCaptainPlayerId: Player['id'] | undefined;

  for (const teamPlayer of team.players) {
    if (teamPlayer.positionSlot === null) {
      continue;
    }

    const player = playersById.get(String(teamPlayer.fantasyPlayerId));
    if (!player) {
      continue;
    }

    playersBySlot.set(teamPlayer.positionSlot, player);
    if (!captainPlayerId && teamPlayer.isCaptain) {
      captainPlayerId = player.id;
    }
    if (!viceCaptainPlayerId && teamPlayer.isViceCaptain) {
      viceCaptainPlayerId = player.id;
    }
  }

  if (!captainPlayerId && team.captainFantasyPlayerId) {
    captainPlayerId = playersById.get(String(team.captainFantasyPlayerId))?.id;
  }

  if (!viceCaptainPlayerId && team.viceCaptainFantasyPlayerId) {
    viceCaptainPlayerId = playersById.get(String(team.viceCaptainFantasyPlayerId))?.id;
  }

  return {
    teamId: team.id,
    teamName: team.teamName,
    formation: parsedFormation,
    budgetRemaining: team.budgetRemaining,
    transfersUsed: team.transfersUsed,
    pendingTransferCost: 0,
    playersBySlot,
    captainPlayerId,
    viceCaptainPlayerId,
  };
};
